import { useState, useEffect, useRef } from "react";
import { MenuCategory } from "@/types/menu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { translateMenu } from "@/lib/gemini/client";
import { useUserStore } from "@/store/userStore";
import { cn } from "@/lib/utils";
import { getLanguageInfo } from "@/lib/languages";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import * as flags from "country-flag-icons/react/3x2";
import { trackApiErrorToast } from "@/lib/mixpanel-client";

type TranslateSectionDialogProps = {
  category: MenuCategory;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedCategory: MenuCategory) => void;
  supportedLanguages: string[];
  language: string;
};

export default function TranslateSectionDialog({
  category,
  isOpen,
  onClose,
  onUpdate,
  supportedLanguages,
  language,
}: TranslateSectionDialogProps) {
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showCancelTranslationAlert, setShowCancelTranslationAlert] =
    useState(false);
  const [progress, setProgress] = useState(0);
  const [currentLanguage, setCurrentLanguage] = useState("");
  const { toast } = useToast();
  const { getRemainingCredits } = useUserStore();
  const remainingCredits = getRemainingCredits();
  const t = useTranslations("TranslateSectionDialog");
  const tLang = useTranslations("LanguageNames");
  const abortControllerRef = useRef<AbortController | null>(null);

  const sourceLanguage = language;

  const availableLanguages = supportedLanguages.filter(
    (lang) => lang !== sourceLanguage
  );

  useEffect(() => {
    if (isOpen && availableLanguages.length === 0) {
      toast({
        title: t("noLanguagesToTranslate"),
        description: t("allLanguagesTranslated"),
        variant: "default",
      });
      onClose();
    }
  }, [isOpen, availableLanguages.length, onClose, toast, t]);

  const handleDialogOpenChange = (open: boolean) => {
    if (!open && isTranslating) {
      // If trying to close while translating, show confirmation dialog
      setShowCancelTranslationAlert(true);
      return;
    }

    if (!open) {
      // If we're closing the dialog, make sure any pending translation is canceled
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      onClose();
    }
  };

  const handleCancelTranslation = () => {
    // Abort the Gemini API request if it's in progress
    if (abortControllerRef.current) {
      console.log("Aborting Gemini API translation request");
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Set these states immediately to show UI feedback
    setIsTranslating(false);
    setShowCancelTranslationAlert(false);
    onClose();

    // Show a confirmation toast with default styling (not error)
    toast({
      title: t("translationCancelled"),
      description: t("translationCancelledDescription"),
      variant: "default",
    });
  };

  // Add a handler function to check if an error is from an abortion
  const isAbortError = (error: unknown): boolean => {
    return (
      error instanceof Error &&
      (error.name === "AbortError" ||
        error.message === "Translation cancelled" ||
        error.message.includes("cancelled") || // More general catch for "cancelled" messages
        error.message.includes("abort") || // More general catch for "abort" messages
        error.message.includes("signal is aborted") ||
        (error.message.includes("Error fetching") &&
          error.message.includes("signal is aborted")))
    );
  };

  // Add a custom logging helper to suppress abort-related errors
  const suppressConsoleErrors = (error: unknown): boolean => {
    if (isAbortError(error)) {
      // Instead of console.error, use console.log with a more user-friendly message
      console.log("Translation cancelled - suppressing error message");

      // If the error is specifically a "Translation cancelled" error from our client.ts file,
      // we'll completely ignore it since we already show a user-friendly toast
      if (error instanceof Error && error.message === "Translation cancelled") {
        return true;
      }

      return true; // Error was suppressed
    }
    return false; // Error was not suppressed
  };

  const handleTranslate = async () => {
    if (selectedLanguages.length === 0) {
      const errTitle = t("noLanguagesSelected");
      const errDesc = t("selectAtLeastOne");
      toast({
        variant: "destructive",
        title: errTitle,
        description: errDesc,
      });
      trackApiErrorToast(
        "translation/validation-error",
        `${errTitle}: ${errDesc}`,
        undefined
      );
      return;
    }

    const totalRequiredCredits = selectedLanguages.length;
    if (remainingCredits < totalRequiredCredits) {
      const errTitle = t("insufficientCredits");
      const errDesc = t("notEnoughCreditsMessage", {
        needed: totalRequiredCredits,
        available: remainingCredits,
      });
      toast({
        variant: "destructive",
        title: errTitle,
        description: errDesc,
      });
      trackApiErrorToast(
        "translation/validation-error",
        `${errTitle}: ${errDesc}`,
        undefined
      );
      return;
    }

    // Create new AbortController for this translation session
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsTranslating(true);
    const updatedCategory = JSON.parse(
      JSON.stringify(category)
    ) as MenuCategory;
    let translationSucceeded = true;
    let successCount = 0;
    const successfulTranslations: string[] = [];
    let wasManuallyAborted = false;

    try {
      // Create a copy of the menu to avoid modifying the original
      const tempMenu = {
        id: category.id,
        title: {}, // Empty title object
        categories: [category], // Only include the current category
        supportedLanguages: supportedLanguages,
      };

      // Process each selected language
      for (const [index, targetLang] of selectedLanguages.entries()) {
        setProgress((index / selectedLanguages.length) * 100);
        setCurrentLanguage(targetLang);

        try {
          // Check if the operation was aborted
          if (signal.aborted) {
            console.log("Translation operation was aborted");
            wasManuallyAborted = true;
            break;
          }

          // Pass the signal to translateMenu
          const translatedMenu = await translateMenu(
            tempMenu,
            targetLang,
            undefined,
            signal
          );

          // Extract the translated category
          const translatedCategory = translatedMenu.categories[0];

          if (translatedCategory) {
            successfulTranslations.push(targetLang);
            successCount++;

            // Update the name in the target language
            if (translatedCategory.name[targetLang]?.trim()) {
              updatedCategory.name[targetLang] =
                translatedCategory.name[targetLang];
            }

            // Update the description in the target language if it exists
            if (translatedCategory.description?.[targetLang]?.trim()) {
              if (!updatedCategory.description)
                updatedCategory.description = {};
              updatedCategory.description[targetLang] =
                translatedCategory.description[targetLang];
            }

            // Update all items in the category
            updatedCategory.items = category.items.map((item, itemIndex) => {
              const translatedItem = translatedCategory.items?.[itemIndex];
              if (!translatedItem) return item;

              const mergedItem = { ...item };
              if (translatedItem.name[targetLang]?.trim()) {
                mergedItem.name[targetLang] = translatedItem.name[targetLang];
              }
              if (translatedItem.description?.[targetLang]?.trim()) {
                if (!mergedItem.description) mergedItem.description = {};
                mergedItem.description[targetLang] =
                  translatedItem.description[targetLang];
              }
              return mergedItem;
            });
          }
        } catch (error: unknown) {
          // Check if this is an abort error, which shouldn't be treated as a failure
          if (isAbortError(error)) {
            console.log("Translation aborted during processing");
            wasManuallyAborted = true;
            // Exit the entire translation process, not just this language iteration
            break;
          }

          translationSucceeded = false;
          // Only log the error if it's not a cancellation
          if (!suppressConsoleErrors(error)) {
            console.error(`Translation to ${targetLang} failed:`, error);
          }

          // Only display error toast when not intentionally cancelled
          if (!signal.aborted && !wasManuallyAborted) {
            const langLabel = getLanguageInfo(targetLang)?.label || targetLang;
            const errorMessage =
              error instanceof Error
                ? error.message
                : "Unknown translation error";
            toast({
              variant: "destructive",
              title: t("translationError", { language: langLabel }),
              description: errorMessage,
            });
            trackApiErrorToast(
              "gemini/translateMenu",
              errorMessage,
              // @ts-expect-error error might have status
              error?.status,
              { "Target Language": targetLang }
            );
          }
        }
      }

      // Check if translation was cancelled after processing languages
      if (signal.aborted || wasManuallyAborted) {
        console.log("Translation was cancelled, skipping further processing");
        return;
      }

      // Only show success messages if we haven't been aborted
      if (!signal.aborted) {
        // Update the category with translations if any were successful
        if (successfulTranslations.length > 0) {
          await onUpdate(updatedCategory);
        }

        if (translationSucceeded && successCount > 0) {
          toast({
            title: t("sectionTranslated"),
            description: t("successfullyTranslated", {
              count: successCount,
              plural: successCount === 1 ? "" : "s",
            }),
          });
          onClose();
        } else if (successCount > 0) {
          toast({
            title: t("sectionPartiallyTranslated"),
            description: t("partialTranslationSuccess", {
              count: successCount,
              plural: successCount === 1 ? "" : "s",
            }),
          });
          onClose();
        } else if (successCount === 0 && !signal.aborted) {
          const errTitle = t("translationFailed");
          const errDesc = t("pleaseTryAgain");
          toast({
            variant: "destructive",
            title: errTitle,
            description: errDesc,
          });
          trackApiErrorToast(
            "gemini/translateMenu", // Overall process failure likely due to individual errors
            `${errTitle}: ${errDesc}`,
            undefined, // Status code not directly available here
            { "Selected Languages": selectedLanguages.join(", ") }
          );
        }
      }
    } catch (error: unknown) {
      // Check if this is an intentional abort
      if (isAbortError(error)) {
        console.log("Translation process was cancelled by user");
        wasManuallyAborted = true;
        // Don't show any error messages for intentional cancellations
        return;
      }

      // Handle top-level errors that aren't related to individual language translations
      // Only log the error if it's not a cancellation
      if (!suppressConsoleErrors(error)) {
        console.error("Translation process failed:", error);
      }

      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      // Only show error toast if not manually aborted
      if (!wasManuallyAborted && !signal.aborted) {
        const errTitle = t("translationFailed");
        toast({
          variant: "destructive",
          title: errTitle,
          description: errorMessage,
        });
        trackApiErrorToast(
          "gemini/translateMenu", // General failure in the translation process
          `${errTitle}: ${errorMessage}`,
          // @ts-expect-error error might have status
          error?.status
        );
      }
    } finally {
      // Clean up regardless of success or failure
      setIsTranslating(false);
      setProgress(0);
      setCurrentLanguage("");

      // Clear the abort controller reference since we're done
      // But first check if we're cleaning up after an abort or normal completion
      const wasAborted = abortControllerRef.current?.signal.aborted;
      abortControllerRef.current = null;

      // If this was an abort, make sure we don't show any error toasts
      if (wasAborted || wasManuallyAborted) {
        console.log("Cleaning up after translation was cancelled");
        // We've already shown the cancellation toast in handleCancelTranslation
      }
    }
  };

  // Clean up on unmount and whenever abortControllerRef changes
  useEffect(() => {
    const controller = abortControllerRef.current;

    return () => {
      if (controller) {
        // Make sure to safely abort any in-progress requests when unmounting
        try {
          controller.abort();
        } catch (error) {
          console.error("Error aborting controller during cleanup:", error);
        }
      }
    };
  }, []);

  // Get button text based on selected languages
  const getButtonText = () => {
    if (isTranslating) return t("translating");
    if (selectedLanguages.length === 0) return t("selectLanguageToTranslate");
    if (selectedLanguages.length === 1) {
      return t("translateSectionInto", {
        language: tLang(selectedLanguages[0].toLowerCase()),
      });
    }
    return t("translateToLanguages", { count: selectedLanguages.length });
  };

  // Helper function to get country code for a language
  const getCountryCode = (languageCode: string): string => {
    // Special cases for Mandarin variants
    if (
      languageCode.toLowerCase() === "chinese_simplified" ||
      languageCode.toLowerCase() === "simplified_chinese" ||
      languageCode.toLowerCase() === "chinese-simplified"
    ) {
      return "CN"; // China flag for Simplified Chinese
    }
    if (
      languageCode.toLowerCase() === "chinese_traditional" ||
      languageCode.toLowerCase() === "traditional_chinese" ||
      languageCode.toLowerCase() === "chinese-traditional"
    ) {
      return "TW"; // Taiwan flag for Traditional Chinese
    }

    const countryMap: Record<string, string> = {
      arabic: "SA",
      bhojpuri: "IN",
      cantonese: "HK",
      chinese: "CN", // Generic Chinese
      chinese_simplified: "CN",
      chinese_traditional: "TW",
      czech: "CZ",
      dutch: "NL",
      english: "GB",
      filipino: "PH",
      french: "FR",
      german: "DE",
      hebrew: "IL",
      hindi: "IN",
      indonesian: "ID",
      italian: "IT",
      japanese: "JP",
      korean: "KR",
      malay: "MY",
      mandarin: "CN", // Generic Mandarin
      mandarin_simplified: "CN",
      mandarin_traditional: "TW",
      persian: "IR",
      polish: "PL",
      portuguese: "PT",
      portuguese_brasil: "BR",
      portuguese_brasileiro: "BR",
      português_brasil: "BR",
      português_brasileiro: "BR",
      romanian: "RO",
      russian: "RU",
      simplified_chinese: "CN",
      slovenian: "SI",
      spanish: "ES",
      swedish: "SE",
      thai: "TH",
      traditional_chinese: "TW",
      turkish: "TR",
      vietnamese: "VN",
    };

    // Special check for Brazilian Portuguese variants
    if (
      languageCode.toLowerCase().includes("portuguese") ||
      languageCode.toLowerCase().includes("português")
    ) {
      if (
        languageCode.toLowerCase().includes("brasil") ||
        languageCode.toLowerCase().includes("brazil") ||
        languageCode.toLowerCase().includes("brasileiro")
      ) {
        return "BR";
      }
    }

    return countryMap[languageCode.toLowerCase()] || "US"; // Default to US flag
  };

  // Get localized language name
  const getLocalizedLanguageName = (code: string): string => {
    // Special cases for languages with variants
    if (code === "chinese-simplified" || code === "chinese_simplified") {
      return tLang("chinese-simplified");
    }
    if (code === "chinese-traditional" || code === "chinese_traditional") {
      return tLang("chinese-traditional");
    }

    // Try to get translation from the LanguageNames namespace
    try {
      return tLang(code.toLowerCase());
    } catch {
      // Fallback to the language name from getLanguageInfo if translation is missing
      return getLanguageInfo(code)?.label || code;
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="p-0 sm:p-6 h-[100dvh] w-[100dvw] sm:h-auto sm:w-auto sm:min-w-[500px] sm:max-w-[600px] sm:max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0 p-6 pb-4 sm:p-0 sm:pb-2">
            <DialogTitle>{t("title")}</DialogTitle>
            <DialogDescription className="text-pretty">
              {t("description")}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-grow px-6 sm:px-0 py-4">
            <div className="grid gap-4">
              <div className="space-y-4">
                {isTranslating && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        {currentLanguage && (
                          <div className="flex items-center gap-1">
                            <span>
                              {t("translating")}:{" "}
                              {tLang(currentLanguage.toLowerCase())}
                            </span>
                          </div>
                        )}
                      </div>
                      <div>{Math.round(progress)}%</div>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-primary h-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <Label>{t("availableLanguages")}</Label>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="select-all-languages"
                      checked={
                        selectedLanguages.length === availableLanguages.length
                      }
                      onCheckedChange={(checked) => {
                        if (checked) {
                          if (availableLanguages.length > remainingCredits) {
                            // If not enough credits for all languages, select only what they can afford
                            const languagesToSelect = availableLanguages.slice(
                              0,
                              remainingCredits
                            );
                            setSelectedLanguages(languagesToSelect);

                            toast({
                              variant: "default",
                              title: t("limitedCreditsTitle"),
                              description: t("limitedCreditsDescription", {
                                count: remainingCredits,
                                plural: remainingCredits === 1 ? "" : "s",
                              }),
                            });
                          } else {
                            // Select all languages
                            setSelectedLanguages([...availableLanguages]);
                          }
                        } else {
                          // Deselect all languages
                          setSelectedLanguages([]);
                        }
                      }}
                    />
                    <Label htmlFor="select-all-languages" className="text-sm">
                      {t("selectAll")}
                    </Label>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {availableLanguages.map((lang) => {
                    const langInfo = getLanguageInfo(lang);
                    const countryCode = getCountryCode(lang);
                    const FlagComponent =
                      flags[countryCode as keyof typeof flags];
                    const isDisabled =
                      !selectedLanguages.includes(lang) &&
                      selectedLanguages.length >= remainingCredits;

                    return (
                      <div
                        key={lang}
                        className={cn(
                          "flex items-center p-2 px-4 bg-muted/50 rounded-md",
                          isDisabled && "opacity-50"
                        )}
                      >
                        <Checkbox
                          id={`lang-${lang}`}
                          checked={selectedLanguages.includes(lang)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              if (
                                selectedLanguages.length >= remainingCredits
                              ) {
                                toast({
                                  variant: "destructive",
                                  title: t("insufficientCredits"),
                                  description: t("notEnoughCreditsMessage"),
                                });
                                return;
                              }
                              setSelectedLanguages([
                                ...selectedLanguages,
                                lang,
                              ]);
                            } else {
                              setSelectedLanguages(
                                selectedLanguages.filter((l) => l !== lang)
                              );
                            }
                          }}
                          disabled={isDisabled}
                          className="mr-2"
                        />
                        <div className="flex-shrink-0 mx-2">
                          {FlagComponent && (
                            <FlagComponent className="w-6 h-4 rounded shadow-sm" />
                          )}
                        </div>
                        <div className="flex flex-col flex-1">
                          <Label
                            htmlFor={`lang-${lang}`}
                            className={cn(
                              "text-sm font-medium",
                              isDisabled && "text-muted-foreground"
                            )}
                          >
                            {langInfo?.nativeName}
                          </Label>
                          <span className="text-xs text-muted-foreground">
                            {getLocalizedLanguageName(lang)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {remainingCredits === 0 ? (
                  <div className="text-sm text-destructive">
                    {t("noCreditsAvailable")}{" "}
                    <Link
                      href="/dashboard/settings/plan#purchase-credits"
                      className="underline"
                    >
                      {t("upgradePlan")}
                    </Link>{" "}
                    {t("orText")}{" "}
                    <Link
                      href="/dashboard/settings/plan#purchase-credits"
                      className="underline"
                    >
                      {t("purchaseCredits")}
                    </Link>
                    .
                  </div>
                ) : remainingCredits < availableLanguages.length ? (
                  <div className="text-sm text-destructive">
                    {t("creditsRemaining", {
                      count: remainingCredits,
                      plural: remainingCredits === 1 ? "" : "s",
                    })}{" "}
                    <Link
                      href="/dashboard/settings/plan#purchase-credits"
                      className="underline"
                    >
                      {t("upgradePlan")}
                    </Link>{" "}
                    {t("orText")}{" "}
                    <Link
                      href="/dashboard/settings/plan#purchase-credits"
                      className="underline"
                    >
                      {t("purchaseCredits")}
                    </Link>
                    .
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground mt-2">
                    {t("totalCreditsRemaining", { count: remainingCredits })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-shrink-0 p-6 pt-4 sm:p-0 mt-auto border-t sm:border-0">
            <div className="flex gap-2 w-full sm:justify-end">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 sm:flex-none"
                disabled={isTranslating}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleTranslate}
                disabled={isTranslating || selectedLanguages.length === 0}
                className="flex-1 sm:flex-none"
              >
                {getButtonText()}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={showCancelTranslationAlert}
        onOpenChange={setShowCancelTranslationAlert}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cancelTranslation")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("cancelTranslationDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel
              className="w-full sm:w-auto"
              onClick={() => setShowCancelTranslationAlert(false)}
            >
              {t("continueTranslation")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleCancelTranslation}
            >
              {t("cancel")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
